import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import ClientDebtPreview from "../components/ClientDebtPreview";
import {
  AlertTriangle,
  ExternalLink,
  Eye,
  RefreshCw,
  Calendar,
  CheckCircle2,
  Clock,
  Flame,
  Search,
  Send,
  Users,
  UserCircle,
} from "lucide-react";

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
const fmtK = (n) => {
  const v = Math.round(n || 0);
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${v}`;
};

const statusFor = (row, asOf) => {
  if (row.is_paid) return { key: "paid", label: "Paid", tint: "emerald" };
  // Unpaid 90+ days after the invoice — written off as a loss, not expected cash.
  if (row._src === "written_off") {
    return { key: "written_off", label: `Written off · ${row.days_since_invoice ?? "90+"}d`, tint: "dead" };
  }
  if (!row.payment_request_id) {
    // Differentiate the soft pipeline by contract status — same key
    // (so the filter still works as one cohort) but the badge tells the
    // user at a glance whether this is committed or wishful.
    return row.is_signed_project
      ? { key: "not_demanded", label: "Signed · no demand", tint: "sky" }
      : { key: "not_demanded", label: "Unsigned · proposal", tint: "zinc" };
  }
  const due = row.invoice_due_date ? new Date(row.invoice_due_date) : null;
  const today = asOf ? new Date(asOf) : new Date();
  if (due && today > due) {
    const daysLate = Math.floor((today - due) / 86400000);
    return { key: "late", label: `Late +${daysLate}d`, tint: "red", daysLate };
  }
  return { key: "within_terms", label: "Within terms", tint: "amber" };
};

const STATUS_TINTS = {
  emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  red: "bg-red-500/15 text-red-300 border-red-500/40",
  sky: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  zinc: "bg-zinc-500/10 text-zinc-400 border-zinc-700",
  dead: "bg-red-950/40 text-red-400/80 border-red-900/60",
};

const lateBadgeTooltip = (it, st, asOf) => {
  const sent = (it.invoice_sent_at || "").slice(0, 10);
  const due = it.invoice_due_date || "—";
  const terms = it.payment_terms_days || 5;
  if (st.key === "late") {
    return `Demanded ${sent} · payment terms ${terms}d · due ${due} · today ${asOf} → late by ${st.daysLate} day(s)`;
  }
  if (st.key === "within_terms") {
    return `Demanded ${sent} · payment terms ${terms}d · due ${due} · ${it.days_left ?? "—"} day(s) left`;
  }
  if (st.key === "paid") return `Paid ${(it.paid_date || "").slice(0, 10)}`;
  if (st.key === "written_off") {
    return `Invoice sent ${sent} and still unpaid after 90 days — counted as a loss in P&L (Bad debt). Clears itself if the client pays.`;
  }
  return "No demand sent yet — soft pipeline only";
};

const penaltyTooltip = (it, settings) => {
  if (it.penalty) {
    const p = it.penalty;
    const baseLabel = p.base === "project_total" ? "contract total" : "invoice amount";
    return `${p.rate_per_day}%/day × ${p.days_charged} day(s) on ${baseLabel} ($${(p.base_amount || 0).toLocaleString()}) = ${fmt(p.amount)}. Grace ${p.grace_period_days}d after due date. Days overdue: ${p.days_overdue}.`;
  }
  if (!settings?.enabled) {
    return "Penalty calculation is currently OFF. Enable it at /settings/payment-penalty to start accruing penalty on overdue demands.";
  }
  return `No penalty yet — within the ${settings.default_grace_period_days}-day grace period after due date, or rate is 0.`;
};

const CashflowForecastPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState("list");
  // Paid demands are closed business — never shown in the active list
  // (the Paid 60d total still surfaces in the analytics tab if needed).
  // Soft pipeline (no demand sent yet) is split by contract status: signed
  // = real commitment, unsigned = manager's wishful expectation. Both are
  // hidden by default so the active list shows only what's actually owed.
  const [showSigned, setShowSigned] = useState(false);
  const [showUnsigned, setShowUnsigned] = useState(false);
  const [showWrittenOff, setShowWrittenOff] = useState(true);
  const [search, setSearch] = useState("");
  const [filterManager, setFilterManager] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [busy, setBusy] = useState({});
  // Bulk Mark Sent — selection state for the "I already emailed PDFs out-of-band,
  // just record they're sent" flow. Distinct from sendDemand (which creates a
  // payment_request and emails the client). Bulk only operates on not-demanded
  // rows; the canonical demand-sent invariant treats this as "sent-only-manual"
  // (legitimate, no payment_request needed).
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  // "View as client" read-only mirror modal — {client_id, client_name} or null.
  const [viewAs, setViewAs] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/cashflow/forecast");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Send a payment_request straight from cashflow — same backend the green
  // button on /sales/project/<id> uses. Available on not-demanded rows so a
  // manager can act from this page when convenient.
  const sendDemand = async (row) => {
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      await api.post("/client-requests", {
        client_id: row.client_id,
        project_id: row.project_id,
        service_id: row.id,
        request_type: "payment_request",
        title: `Payment Request: ${row.name}`,
        description: `Please process payment of ${row.amount?.toLocaleString() || 0} ${row.currency} for ${row.name}`,
        ball_in_court: "client",
        expected_action: "payment",
        payment_amount: row.amount,
        payment_currency: row.currency,
      });
      toast.success("Payment demand sent");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send demand");
    } finally {
      setBusy((b) => ({ ...b, [row.id]: false }));
    }
  };

  // Bulk mark selected services as invoice_sent — for the case where PDFs were
  // emailed out-of-band and the manager just needs to record it in the system
  // (so they enter Plan/Realistic and the payment-terms clock starts).
  // Does NOT create payment_request — that's sendDemand's job. See backend
  // /sales-services/bulk-mark-sent for full semantics.
  const bulkMarkSent = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      toast.error("Select at least one row first");
      return;
    }
    if (!window.confirm(
      `Mark ${ids.length} payment${ids.length === 1 ? "" : "s"} as invoice-sent today?\n\nThis records that you've already emailed the PDF to the client. Payment-terms clock starts now. Already-sent and already-paid rows are skipped automatically.`
    )) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/sales-services/bulk-mark-sent", { ids });
      const { marked, skipped_already_sent, skipped_paid, skipped_missing } = res.data || {};
      const parts = [`Marked ${marked || 0} as sent`];
      if (skipped_already_sent) parts.push(`${skipped_already_sent} already sent`);
      if (skipped_paid) parts.push(`${skipped_paid} already paid`);
      if (skipped_missing) parts.push(`${skipped_missing} missing`);
      toast.success(parts.join(" · "));
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to bulk-mark");
    } finally {
      setBulkBusy(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const allRows = useMemo(() => {
    if (!data) return [];
    // Paid bucket excluded entirely — the active list is for outstanding
    // demand only; "what's already in the bank" is a different question
    // tracked via the analytics tab and the Paid stats elsewhere.
    const overdue = (data.buckets?.overdue || []).map((it) => ({ ...it, _src: "demand" }));
    const pending = (data.buckets?.pending || []).map((it) => ({ ...it, _src: "demand" }));
    const notDemanded = (data.buckets?.not_invoiced || []).map((it) => ({ ...it, _src: "not_demanded" }));
    const writtenOff = (data.buckets?.written_off || []).map((it) => ({ ...it, _src: "written_off" }));
    return [...overdue, ...pending, ...notDemanded, ...writtenOff];
  }, [data]);

  // Soft pipeline split — count + $ totals to label the two toggles.
  const softTotals = useMemo(() => {
    const signed = { count: 0, total: 0 };
    const unsigned = { count: 0, total: 0 };
    (data?.buckets?.not_invoiced || []).forEach((it) => {
      const bucket = it.is_signed_project ? signed : unsigned;
      bucket.count += 1;
      bucket.total += it.amount_usd || 0;
    });
    return { signed, unsigned };
  }, [data]);

  const managerOptions = useMemo(() => {
    const set = new Map();
    allRows.forEach((r) => {
      if (r.sales_manager_name) set.set(r.sales_manager_name, true);
    });
    return Array.from(set.keys()).sort();
  }, [allRows]);

  const clientOptions = useMemo(() => {
    const set = new Map();
    allRows.forEach((r) => {
      if (r.client_name && r.client_name !== "—") set.set(r.client_name, true);
    });
    return Array.from(set.keys()).sort();
  }, [allRows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows
      .filter((r) => {
        const st = statusFor(r, data?.as_of);
        // Soft pipeline split by contract status — each cohort hidden
        // until the user explicitly toggles it on.
        if (st.key === "not_demanded") {
          if (r.is_signed_project && !showSigned) return false;
          if (!r.is_signed_project && !showUnsigned) return false;
        }
        if (st.key === "written_off" && !showWrittenOff) return false;
        if (filterManager !== "all" && (r.sales_manager_name || "") !== filterManager) return false;
        if (filterClient !== "all" && (r.client_name || "") !== filterClient) return false;
        if (q) {
          const hay = `${r.project_name || ""} ${r.client_name || ""} ${r.name || ""} ${r.sales_manager_name || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .map((r) => ({ ...r, _status: statusFor(r, data?.as_of) }))
      .sort((a, b) => {
        const groupOrder = { late: 0, within_terms: 1, not_demanded: 2, written_off: 3 };
        const ga = groupOrder[a._status.key];
        const gb = groupOrder[b._status.key];
        if (ga !== gb) return ga - gb;
        const da = a.invoice_due_date || a.paid_date || "9999-99-99";
        const db = b.invoice_due_date || b.paid_date || "9999-99-99";
        return da.localeCompare(db);
      });
  }, [allRows, showSigned, showUnsigned, showWrittenOff, search, filterManager, filterClient, data?.as_of]);

  const stats = useMemo(() => {
    // "Obligated" = everything currently on the table, no historical income.
    //   demandedOutstanding — invoices issued and still unpaid
    //                         (late + within terms)
    //   + signedNoDemand    — committed under contract, invoice not yet
    //                         issued (= softTotals.signed.total)
    //   + unsignedTotal     — proposals out, contracts not yet signed
    //                         (= softTotals.unsigned.total)
    // Paid-60d is past income, not an obligation — excluded from this KPI.
    const lateTotal = (data?.summary?.overdue_total || 0);
    const latePenalty = (data?.summary?.overdue_penalty_total || 0);
    const withinTermsTotal = (data?.summary?.pending_total || 0);
    const notDemandedTotal = (data?.summary?.not_invoiced_total || 0);
    const demandedOutstanding = lateTotal + withinTermsTotal;
    const signedNoDemandTotal = softTotals.signed.total;
    const unsignedTotal = softTotals.unsigned.total;
    const totalObligated = demandedOutstanding + signedNoDemandTotal + unsignedTotal;
    return {
      totalObligated, demandedOutstanding, signedNoDemandTotal, unsignedTotal,
      lateTotal, latePenalty, withinTermsTotal, notDemandedTotal,
    };
  }, [data, softTotals]);

  const expectedByMonth = data?.expected_by_month || [];
  const penaltySettings = data?.penalty_settings || { enabled: false };
  const byClient = data?.by_client || [];
  const byManager = data?.by_manager || [];
  const overdueClients = byClient.filter((c) => c.late_total > 0);
  const obligatedByClient = byClient
    .filter((c) => c.late_total + c.within_total > 0)
    .sort((a, b) => -(a.late_total + a.within_total) + (b.late_total + b.within_total));

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-semibold">Failed to load cashflow</span>
        </div>
        <p className="text-sm text-red-300/70 mt-2 font-mono">{error}</p>
      </div>
    );
  }

  return (
    <div data-testid="cashflow-forecast-page" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Cashflow</h1>
          <p className="text-sm text-zinc-500">
            Read-only view of payment demands. The primary entry point is the green
            <span className="inline-flex items-center mx-1 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[11px] font-mono">Send</span>
            button inside each project. As of {data?.as_of} · default terms{" "}
            <span className="font-mono text-zinc-300">{data?.default_payment_terms_days}d</span>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800">
          <RefreshCw className="h-3 w-3 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Loud banner when penalty is off but there are overdue demands — answers
          the common "why isn't penalty accruing on my payment requests?" report. */}
      {!penaltySettings.enabled && (data?.summary?.overdue_count || 0) > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2 text-amber-200 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-semibold">Penalty accrual is disabled.</span>{" "}
              {data?.summary?.overdue_count} demand{data?.summary?.overdue_count === 1 ? " is" : "s are"} overdue but no late-payment penalty is being charged.
            </div>
          </div>
          <Link
            to="/settings/payment-penalty"
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 px-3 py-1.5 text-xs font-medium"
          >
            Enable in settings
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        <div title="Everything still on the table — outstanding demands (late + within terms) + signed work without invoice yet + unsigned proposals. Already-received payments are NOT counted.">
          <p className="text-[11px] text-zinc-500 uppercase font-mono tracking-wide">Obligated</p>
          <p className="text-2xl font-bold text-zinc-50">{fmt(stats.totalObligated)}</p>
          <p className="text-[11px] text-zinc-500">
            <span className="text-zinc-300">{fmtK(stats.demandedOutstanding)}</span> demanded
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-sky-300">{fmtK(stats.signedNoDemandTotal)}</span> signed
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-zinc-400">{fmtK(stats.unsignedTotal)}</span> unsigned
          </p>
        </div>
        <div>
          <p className="text-[11px] text-amber-400 uppercase font-mono tracking-wide">Within terms</p>
          <p className="text-2xl font-bold text-zinc-50">{fmt(stats.withinTermsTotal)}</p>
          <p className="text-[11px] text-zinc-500">{data?.summary?.pending_count || 0} demands waiting</p>
        </div>
        <div>
          <p className="text-[11px] text-red-400 uppercase font-mono tracking-wide">Overdue</p>
          <p className="text-2xl font-bold text-zinc-50">{fmt(stats.lateTotal)}</p>
          <p className="text-[11px] text-zinc-500">{data?.summary?.overdue_count || 0} past due</p>
        </div>
        <div title="Invoices still unpaid 90+ days after sending. Not expected any more — counted as a loss (Bad debt) in P&L." data-testid="cashflow-written-off">
          <p className="text-[11px] text-red-400/80 uppercase font-mono tracking-wide">Written off 90+</p>
          <p className="text-2xl font-bold text-red-400">{(data?.summary?.written_off_total || 0) > 0 ? "−" : ""}{fmt(data?.summary?.written_off_total)}</p>
          <p className="text-[11px] text-zinc-500">{data?.summary?.written_off_count || 0} invoices · loss</p>
        </div>
        <div title={penaltyTooltip({}, penaltySettings)} className="cursor-help">
          <p className="text-[11px] text-orange-400 uppercase font-mono tracking-wide">Penalty accrued</p>
          <p className={cn("text-2xl font-bold", penaltySettings.enabled ? "text-zinc-50" : "text-zinc-500")}>
            {penaltySettings.enabled ? fmt(stats.latePenalty) : "—"}
          </p>
          <p className="text-[11px] text-zinc-500">
            {penaltySettings.enabled
              ? `${penaltySettings.default_rate_per_day}%/day after ${penaltySettings.default_grace_period_days}d grace`
              : "Penalty calc disabled"}
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="list">Demands list</TabsTrigger>
          <TabsTrigger value="analytics">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            By client / manager
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          {expectedByMonth.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-zinc-50 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-500" />
                  Expected receipts by due-date month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {expectedByMonth.map((b) => (
                    <div
                      key={b.month}
                      className={cn(
                        "rounded-md border p-3",
                        b.overdue_total > 0 ? "border-red-900/40 bg-red-950/20" : "border-amber-900/30 bg-amber-950/10"
                      )}
                    >
                      <p className="text-xs text-zinc-400 font-mono uppercase tracking-wide">
                        {b.month === "unscheduled" ? "No date" : b.month}
                      </p>
                      <p className="text-lg font-bold text-zinc-50 mt-1">{fmtK(b.total)}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {b.count} demand{b.count === 1 ? "" : "s"}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3 px-1">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                <Input
                  placeholder="Search project, client, manager…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 w-[260px] bg-zinc-950 border-zinc-800 text-sm text-zinc-50"
                />
              </div>
              <Select value={filterManager} onValueChange={setFilterManager}>
                <SelectTrigger className="h-8 w-[180px] bg-zinc-950 border-zinc-800 text-sm text-zinc-50">
                  <UserCircle className="h-3.5 w-3.5 mr-1.5 text-zinc-500" />
                  <SelectValue placeholder="Manager / PM" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-sm">
                  <SelectItem value="all">All managers</SelectItem>
                  {managerOptions.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="h-8 w-[180px] bg-zinc-950 border-zinc-800 text-sm text-zinc-50">
                  <Users className="h-3.5 w-3.5 mr-1.5 text-zinc-500" />
                  <SelectValue placeholder="Client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-sm">
                  <SelectItem value="all">All clients</SelectItem>
                  {clientOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label
                className="flex items-center gap-2 text-xs text-zinc-400"
                title="Project status ≥ contract_signed but no payment_request issued yet — committed money, you just haven't sent the demand."
              >
                <Switch checked={showSigned} onCheckedChange={setShowSigned} />
                <span>Show signed (no demand) <span className="text-emerald-300/80">({softTotals.signed.count} · {fmtK(softTotals.signed.total)})</span></span>
              </label>
              <label
                className="flex items-center gap-2 text-xs text-zinc-400"
                title="Project still at proposal_sent / contract_negotiation — manager's expectation, not yet committed."
              >
                <Switch checked={showUnsigned} onCheckedChange={setShowUnsigned} />
                <span>Show unsigned (proposals) <span className="text-zinc-400">({softTotals.unsigned.count} · {fmtK(softTotals.unsigned.total)})</span></span>
              </label>
              {(data?.summary?.written_off_count || 0) > 0 && (
                <label
                  className="flex items-center gap-2 text-xs text-zinc-400"
                  title="Invoices still unpaid 90+ days after sending — written off as a loss, not expected cash."
                >
                  <Switch checked={showWrittenOff} onCheckedChange={setShowWrittenOff} data-testid="show-written-off" />
                  <span>Show written off <span className="text-red-400/80">({data.summary.written_off_count} · {fmtK(data.summary.written_off_total)})</span></span>
                </label>
              )}
            </div>
            <p className="text-xs text-zinc-500">
              {visibleRows.length} row{visibleRows.length === 1 ? "" : "s"} shown
            </p>
          </div>

          {/* Bulk-mark-sent action bar — appears when rows are selected.
              Selection only available on not-demanded rows; bulk-mark records
              "I emailed the PDF out-of-band" without creating a payment_request. */}
          {selectedCount > 0 && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-emerald-950/30 border border-emerald-900/30">
              <div className="text-xs text-emerald-200">
                <strong className="text-emerald-300">{selectedCount}</strong> row{selectedCount === 1 ? "" : "s"} selected.
                Bulk-mark records that the invoice/PDF was already sent to the client (out-of-band) — payment-terms clock starts today, no email is triggered.
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected({})}
                  disabled={bulkBusy}
                  className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-50"
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={bulkMarkSent}
                  disabled={bulkBusy}
                  className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bulk-mark-sent-btn"
                >
                  {bulkBusy ? "Marking…" : `Mark ${selectedCount} as sent`}
                </Button>
              </div>
            </div>
          )}

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {visibleRows.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-10">Nothing to show with current filters.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs w-[28px] px-1">
                        {/* Header checkbox only renders when there's at least one
                            not-demanded row to select — otherwise it's a dead
                            control that confused users (only paid/overdue rows
                            visible? → no select-all to show). */}
                        {(() => {
                          const candidates = visibleRows.filter((r) => r._status?.key === "not_demanded");
                          if (candidates.length === 0) return null;
                          const allSelected = candidates.every((r) => selected[r.id]);
                          return (
                            <input
                              type="checkbox"
                              aria-label="Select all not-demanded rows"
                              title={`Select all ${candidates.length} not-demanded row${candidates.length === 1 ? "" : "s"} for bulk Mark Sent`}
                              className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 accent-emerald-500 cursor-pointer"
                              checked={allSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const next = { ...selected };
                                  candidates.forEach((r) => { next[r.id] = true; });
                                  setSelected(next);
                                } else {
                                  const next = { ...selected };
                                  candidates.forEach((r) => { delete next[r.id]; });
                                  setSelected(next);
                                }
                              }}
                            />
                          );
                        })()}
                      </TableHead>
                      <TableHead className="text-zinc-500 text-xs">Service</TableHead>
                      <TableHead className="text-zinc-500 text-xs">Client</TableHead>
                      <TableHead className="text-zinc-500 text-xs">Manager</TableHead>
                      <TableHead className="text-zinc-500 text-xs">Demanded</TableHead>
                      <TableHead className="text-zinc-500 text-xs">Due</TableHead>
                      <TableHead className="text-zinc-500 text-xs">Status</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Amount</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Penalty</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right w-[120px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleRows.map((it) => {
                      const st = it._status;
                      const isPaid = st.key === "paid";
                      const isNotDemanded = st.key === "not_demanded";
                      return (
                        <TableRow
                          key={it.id}
                          className={cn("border-zinc-800 hover:bg-zinc-800/30", (isPaid || st.key === "written_off") && "opacity-60")}
                        >
                          <TableCell className="py-2.5 w-[28px] px-1">
                            {isNotDemanded && (
                              <input
                                type="checkbox"
                                aria-label="Select row for bulk mark sent"
                                className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 accent-emerald-500 cursor-pointer"
                                checked={!!selected[it.id]}
                                onChange={(e) => {
                                  const next = { ...selected };
                                  if (e.target.checked) next[it.id] = true;
                                  else delete next[it.id];
                                  setSelected(next);
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell className="py-2.5 text-sm">
                            <div className="text-zinc-50 font-medium">{it.project_name || "—"}</div>
                            <div className="text-zinc-500 text-xs truncate max-w-[260px]">{it.name}</div>
                          </TableCell>
                          <TableCell className="py-2.5 text-sm text-zinc-300">{it.client_name || "—"}</TableCell>
                          <TableCell className="py-2.5 text-sm text-zinc-400">{it.sales_manager_name || "—"}</TableCell>
                          <TableCell className="py-2.5 text-xs font-mono text-zinc-400">
                            {(it.invoice_sent_at || "").slice(0, 10) || "—"}
                            {it.demanded_by_name && (
                              <div className="text-[10px] text-zinc-600 normal-case">
                                by {it.demanded_by_name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="py-2.5 text-xs font-mono text-zinc-400">
                            {it.invoice_due_date || (isPaid ? `paid ${(it.paid_date || "").slice(0, 10)}` : "—")}
                          </TableCell>
                          <TableCell className="py-2.5">
                            <Badge
                              variant="outline"
                              className={cn("font-mono text-xs cursor-help", STATUS_TINTS[st.tint])}
                              title={lateBadgeTooltip(it, st, data?.as_of)}
                            >
                              {st.key === "late" && <Flame className="h-3 w-3 mr-1" />}
                              {st.key === "within_terms" && <Clock className="h-3 w-3 mr-1" />}
                              {st.key === "paid" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {st.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2.5 text-right text-sm font-mono text-zinc-50">
                            {fmt(it.amount_usd)}
                            <div className="text-[10px] text-zinc-600">{it.amount} {it.currency}</div>
                          </TableCell>
                          <TableCell className="py-2.5 text-right">
                            <span
                              title={penaltyTooltip(it, penaltySettings)}
                              className={cn(
                                "font-mono text-sm cursor-help underline decoration-dotted",
                                it.penalty ? "text-red-300 decoration-red-800" : "text-zinc-700 decoration-zinc-800"
                              )}
                            >
                              {it.penalty ? fmt(it.penalty.amount) : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isNotDemanded && (
                                <Button
                                  size="sm"
                                  disabled={busy[it.id]}
                                  onClick={() => sendDemand(it)}
                                  className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                  title="Send payment demand to the client (creates a payment_request — same effect as the green Send button inside the project)"
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Send
                                </Button>
                              )}
                              {it.client_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setViewAs({ client_id: it.client_id, client_name: it.client_name })
                                  }
                                  title="View as client — read-only mirror of their Payments due page (for eyeballing + screenshot)"
                                  className="h-7 px-2 text-xs text-sky-400 hover:text-sky-300"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              )}
                              {it.project_id && (
                                <Link to={`/sales/project/${it.project_id}`} title="Open project">
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-orange-400 hover:text-orange-300">
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {/* Top overdue clients — who's holding up the most cash */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                <Flame className="h-4 w-4 text-red-500" />
                Overdue clients
                <span className="text-xs text-zinc-500 font-normal font-mono">
                  · who's currently holding up our money
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {overdueClients.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">No overdue clients · clean!</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs">Client</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Late demands</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Overdue $</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Penalty</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Within terms</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Recently paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overdueClients.map((c) => (
                      <TableRow key={c.client_id} className="border-zinc-800 hover:bg-zinc-800/30">
                        <TableCell className="py-2.5 text-sm text-zinc-50 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{c.client_name}</span>
                            {c.client_id && (
                              <button
                                type="button"
                                onClick={() =>
                                  setViewAs({ client_id: c.client_id, client_name: c.client_name })
                                }
                                title="View as client — read-only mirror of their Payments due page"
                                className="text-sky-400 hover:text-sky-300"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-red-300 font-mono">{c.late_count}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-red-300 font-mono">{fmt(c.late_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {c.penalty_total > 0 ? <span className="text-red-300">{fmt(c.penalty_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-amber-300 font-mono">{fmt(c.within_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-emerald-300 font-mono">{fmt(c.paid_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Top obligated by client — future revenue impact, signed vs not */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                <Users className="h-4 w-4 text-emerald-500" />
                Future revenue by client
                <span className="text-xs text-zinc-500 font-normal font-mono">
                  · soft pipeline split by contract status
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {obligatedByClient.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">Nothing in the active pipeline.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs">Client</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Within terms</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Late</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Active total</TableHead>
                      <TableHead
                        className="text-zinc-500 text-xs text-right cursor-help"
                        title="Project status ≥ contract_signed — payment is contractually agreed but a payment_request hasn't gone out yet."
                      >
                        Signed (no demand)
                      </TableHead>
                      <TableHead
                        className="text-zinc-500 text-xs text-right cursor-help"
                        title="Project still at proposal_sent / contract_negotiation — manager's expectation, not yet committed."
                      >
                        Unsigned (proposals)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {obligatedByClient.slice(0, 30).map((c) => (
                      <TableRow key={c.client_id} className="border-zinc-800 hover:bg-zinc-800/30">
                        <TableCell className="py-2.5 text-sm text-zinc-50 font-medium">{c.client_name}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-amber-300 font-mono">{fmt(c.within_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {c.late_total > 0 ? <span className="text-red-300">{fmt(c.late_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-zinc-50 font-mono">{fmt(c.late_total + c.within_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {c.not_demanded_signed_total > 0 ? <span className="text-emerald-300">{fmt(c.not_demanded_signed_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {c.not_demanded_unsigned_total > 0 ? <span className="text-zinc-400">{fmt(c.not_demanded_unsigned_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* By manager / PM — pipeline split by signed vs wishful */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                <UserCircle className="h-4 w-4 text-sky-500" />
                Pipeline by manager
                <span className="text-xs text-zinc-500 font-normal font-mono">
                  · "real" excludes unsigned proposals
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {byManager.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">No managers in the pipeline.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500 text-xs">Manager</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Within terms</TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Late</TableHead>
                      <TableHead
                        className="text-zinc-500 text-xs text-right cursor-help"
                        title="Project status ≥ contract_signed but no payment_request issued yet — committed but not yet demanded."
                      >
                        Signed (no demand)
                      </TableHead>
                      <TableHead
                        className="text-zinc-500 text-xs text-right cursor-help"
                        title="Project at proposal_sent / contract_negotiation — manager's expectation, not yet committed."
                      >
                        Unsigned (proposals)
                      </TableHead>
                      <TableHead
                        className="text-zinc-500 text-xs text-right cursor-help"
                        title="Late + within terms + signed soft pipeline. Excludes unsigned proposals — money you can plan around."
                      >
                        Real pipeline
                      </TableHead>
                      <TableHead className="text-zinc-500 text-xs text-right">Recently paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byManager.map((m) => (
                      <TableRow key={m.manager_name} className="border-zinc-800 hover:bg-zinc-800/30">
                        <TableCell className="py-2.5 text-sm text-zinc-50 font-medium">{m.manager_name}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-amber-300 font-mono">{fmt(m.within_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {m.late_total > 0 ? <span className="text-red-300">{fmt(m.late_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {m.not_demanded_signed_total > 0 ? <span className="text-emerald-300">{fmt(m.not_demanded_signed_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm font-mono">
                          {m.not_demanded_unsigned_total > 0 ? <span className="text-zinc-400">{fmt(m.not_demanded_unsigned_total)}</span> : <span className="text-zinc-700">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-zinc-50 font-mono font-semibold">{fmt(m.pipeline_real_total)}</TableCell>
                        <TableCell className="py-2.5 text-right text-sm text-emerald-300 font-mono">{fmt(m.paid_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="text-xs text-zinc-600 text-center pt-1">
        Demands are issued from <span className="text-zinc-400">/sales/project/&lt;id&gt;</span> via the green Send button on each payment row, or directly from the row Action column above for not-demanded services.
      </p>

      {viewAs && (
        <ClientDebtPreview
          clientId={viewAs.client_id}
          clientName={viewAs.client_name}
          onClose={() => setViewAs(null)}
        />
      )}
    </div>
  );
};

export default CashflowForecastPage;
